import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, {
  formatApiError, tThreatCategory, tActorType, tConfidence, tMatrix, tTactic,
  THREAT_CATEGORIES, THREAT_CATEGORY_KEYS, SEVERITY, SEVERITY_KEYS,
  THREAT_STATUS, THREAT_STATUS_KEYS, ACTOR_TYPES, ACTOR_TYPE_KEYS,
  LINK_CONFIDENCE, LINK_CONFIDENCE_KEYS,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import ScenariosPanel from "@/components/shared/ScenariosPanel";
import { SevBadge, ThreatStatusBadge, VulnStatusBadge } from "@/components/shared/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ArrowLeft, Crosshair, ShieldAlert, Info, Link2, Plus, X, Archive, Pencil, Loader2, Target,
} from "lucide-react";

function Field({ label, children, mono }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{label}</dt>
      <dd className={`mt-1 text-sm ${mono ? "font-mono" : ""}`}>{children ?? <span className="text-muted-foreground">—</span>}</dd>
    </div>
  );
}

function Section({ title, icon: Icon, children, testid, action }) {
  return (
    <section className="border border-border bg-card rounded-sm" data-testid={testid}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <h2 className="flex items-center gap-2 font-head font-semibold">{Icon && <Icon className="h-4 w-4 text-primary" />} {title}</h2>
        {action}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

export default function ThreatDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const qc = useQueryClient();
  const writable = can("threat:write");
  const [editOpen, setEditOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [linkForm, setLinkForm] = useState({ vulnerability_id: "", confidence: "MEDIUM", note: "" });
  const [techOpen, setTechOpen] = useState(false);
  const [techRef, setTechRef] = useState("");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["threat", id],
    queryFn: async () => (await api.get(`/threats/${id}`)).data,
  });
  const { data: allVulns = [] } = useQuery({
    queryKey: ["vulnerabilities-min"],
    queryFn: async () => (await api.get("/vulnerabilities")).data,
    enabled: linkOpen,
  });
  const { data: allTechniques = [] } = useQuery({
    queryKey: ["mitre-techniques-min"],
    queryFn: async () => (await api.get("/mitre/techniques")).data,
    enabled: techOpen,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["threat", id] });
    qc.invalidateQueries({ queryKey: ["threats"] });
  };

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...form };
      if (payload.default_actor_type === "NONE") payload.default_actor_type = null;
      return (await api.patch(`/threats/${id}`, payload)).data;
    },
    onSuccess: () => { toast.success("Menace mise à jour"); setEditOpen(false); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const archive = useMutation({
    mutationFn: async () => (await api.delete(`/threats/${id}`)).data,
    onSuccess: (r) => { toast.success(`Menace archivée (statut conservé : ${r.status})`); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const link = useMutation({
    mutationFn: async () => api.post(`/threats/${id}/vulnerabilities/${linkForm.vulnerability_id}`,
      { confidence: linkForm.confidence, note: linkForm.note }),
    onSuccess: () => { toast.success("Vulnérabilité associée"); setLinkOpen(false); setLinkForm({ vulnerability_id: "", confidence: "MEDIUM", note: "" }); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const unlink = useMutation({
    mutationFn: async (vid) => api.delete(`/threats/${id}/vulnerabilities/${vid}`),
    onSuccess: () => { toast.success("Association retirée"); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const linkTech = useMutation({
    mutationFn: async () => api.post(`/threats/${id}/techniques/${encodeURIComponent(techRef)}`, {}),
    onSuccess: () => { toast.success("Technique associée"); setTechOpen(false); setTechRef(""); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const unlinkTech = useMutation({
    mutationFn: async (stix) => api.delete(`/threats/${id}/techniques/${encodeURIComponent(stix)}`),
    onSuccess: () => { toast.success("Technique retirée"); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (isLoading)
    return <AppShell title="Menace"><div className="flex items-center gap-2 text-muted-foreground font-mono text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div></AppShell>;
  if (isError || !data)
    return <AppShell title="Menace"><p className="text-sm text-muted-foreground">Menace introuvable.</p><Button variant="outline" className="mt-4 rounded-sm gap-2" onClick={() => navigate("/threats")}><ArrowLeft className="h-4 w-4" /> Retour</Button></AppShell>;

  const t = data;
  const linkedIds = new Set((t.vulnerabilities || []).map((v) => v.id));
  const linkable = allVulns.filter((v) => !linkedIds.has(v.id));
  const linkedTechStix = new Set((t.techniques || []).map((x) => x.stix_id));
  const linkableTech = allTechniques.filter((x) => !linkedTechStix.has(x.stix_id));

  const openEdit = () => {
    setForm({
      name: t.name, description: t.description || "", category: t.category,
      severity: t.severity, status: t.status, source: t.source || "",
      source_reference: t.source_reference || "", default_actor_type: t.default_actor_type || "NONE",
    });
    setEditOpen(true);
  };

  return (
    <AppShell title={t.name} subtitle="Fiche menace — objet cyber dédié (Lot 2A)"
      actions={
        <div className="flex gap-2">
          {writable && <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={openEdit} data-testid="edit-threat-button"><Pencil className="h-4 w-4" /> Modifier</Button>}
          {writable && !t.archived_at && <Button variant="outline" size="sm" className="rounded-sm gap-2 text-orange-400" onClick={() => { if (window.confirm("Archiver cette menace ? Le statut métier est conservé.")) archive.mutate(); }} data-testid="archive-threat-button"><Archive className="h-4 w-4" /> Archiver</Button>}
          <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={() => navigate("/threats")} data-testid="back-to-threats"><ArrowLeft className="h-4 w-4" /> Menaces</Button>
        </div>
      }>
      <div className="space-y-6" data-testid="threat-detail">
        {t.archived_at && (
          <div className="border border-orange-500/30 bg-orange-500/10 text-orange-300 rounded-sm px-4 py-2 text-sm" data-testid="threat-archived-banner">
            Archivée le {new Date(t.archived_at).toLocaleString("fr-FR")} — statut métier conservé : {THREAT_STATUS[t.status]?.label || t.status}.
          </div>
        )}

        <Section title="Identité" icon={Info} testid="section-threat-identity">
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Field label="Identifiant CROS" mono>{t.id}</Field>
            <Field label="Catégorie">{tThreatCategory(t.category)}</Field>
            <Field label="Gravité"><SevBadge value={t.severity} /></Field>
            <Field label="Statut"><ThreatStatusBadge value={t.status} /></Field>
            <Field label="Type d'acteur (défaut)">{tActorType(t.default_actor_type)}</Field>
            <Field label="Source">{t.source || null}</Field>
            <Field label="Référence source" mono>{t.source_reference || null}</Field>
            <Field label="Première observation" mono>{t.first_observed_at || null}</Field>
            <Field label="Dernière observation" mono>{t.last_observed_at || null}</Field>
            <div className="col-span-2 md:col-span-4"><Field label="Description">{t.description || null}</Field></div>
          </dl>
        </Section>

        <Section title={`Vulnérabilités associées — ${(t.vulnerabilities || []).length}`} icon={ShieldAlert} testid="section-threat-vulnerabilities"
          action={writable && <Button size="sm" className="rounded-sm gap-2" onClick={() => setLinkOpen(true)} data-testid="link-vuln-button"><Plus className="h-4 w-4" /> Associer une vulnérabilité</Button>}>
          <p className="text-xs text-muted-foreground mb-4">Relation explicite <span className="font-mono">THREAT —CAN_EXPLOIT→ VULNERABILITY</span>, déclarée par l'analyste. Cette association ne crée aucun risque, aucun score, aucun scénario.</p>
          {(t.vulnerabilities || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune vulnérabilité associée.</p>
          ) : (
            <div className="border border-border rounded-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-border">
                    <TableHead>Vulnérabilité</TableHead>
                    <TableHead>Gravité</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Confiance</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {t.vulnerabilities.map((v) => (
                    <TableRow key={v.id} className="border-border" data-testid={`threat-vuln-row-${v.id}`}>
                      <TableCell className="font-medium">
                        <button className="flex items-center gap-2 hover:text-primary" onClick={() => navigate(`/vulnerabilities/${v.id}`)} data-testid={`threat-vuln-nav-${v.id}`}>
                          <ShieldAlert className="h-3.5 w-3.5 text-muted-foreground" /> {v.title}
                        </button>
                      </TableCell>
                      <TableCell><SevBadge value={v.severity} /></TableCell>
                      <TableCell><VulnStatusBadge value={v.status} /></TableCell>
                      <TableCell>
                        <Badge variant="outline" className="rounded-sm font-mono text-[10px]">{tConfidence(v.link?.confidence)}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {writable && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" title="Retirer l'association" onClick={() => { if (window.confirm("Retirer cette association ?")) unlink.mutate(v.id); }} data-testid={`threat-vuln-unlink-${v.id}`}><X className="h-4 w-4" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Section>

        <Section title={`Techniques ATT&CK associées — ${(t.techniques || []).length}`} icon={Target} testid="section-threat-techniques"
          action={writable && <Button size="sm" className="rounded-sm gap-2" onClick={() => setTechOpen(true)} data-testid="link-technique-button"><Plus className="h-4 w-4" /> Associer une technique</Button>}>
          <p className="text-xs text-muted-foreground mb-4">Relation sémantique <span className="font-mono">THREAT —USES→ TECHNIQUE</span> (référentiel MITRE dédié, lecture seule). Déclaratif : aucun risque, aucun score, aucun scénario.</p>
          {(t.techniques || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune technique ATT&CK associée.</p>
          ) : (
            <div className="border border-border rounded-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-border">
                    <TableHead className="w-28">Identifiant</TableHead>
                    <TableHead>Technique</TableHead>
                    <TableHead className="w-24">Matrice</TableHead>
                    <TableHead>Tactiques</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {t.techniques.map((tech) => (
                    <TableRow key={tech.stix_id} className="border-border" data-testid={`threat-tech-row-${tech.external_id}`}>
                      <TableCell className="font-mono text-xs text-primary">
                        <button className="hover:underline" onClick={() => navigate("/mitre")} data-testid={`threat-tech-nav-${tech.external_id}`}>{tech.external_id}</button>
                      </TableCell>
                      <TableCell className="font-medium">{tech.name}{tech.is_subtechnique && tech.parent_external_id && <span className="ml-2 text-[10px] font-mono text-muted-foreground">↳ {tech.parent_external_id}</span>}</TableCell>
                      <TableCell><Badge variant="outline" className="rounded-sm font-mono text-[10px]">{tMatrix(tech.matrix)}</Badge></TableCell>
                      <TableCell><div className="flex flex-wrap gap-1">{(tech.tactics || []).map((x) => <Badge key={x} variant="outline" className="rounded-sm text-[10px] font-mono">{tTactic(x)}</Badge>)}</div></TableCell>
                      <TableCell className="text-right">
                        {writable && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" title="Retirer la technique" onClick={() => { if (window.confirm("Retirer cette technique ?")) unlinkTech.mutate(tech.stix_id); }} data-testid={`threat-tech-unlink-${tech.external_id}`}><X className="h-4 w-4" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Section>
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="threat-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Modifier la menace</DialogTitle>
            <DialogDescription>La gravité reste intrinsèque (jamais un risque). Archiver ≠ retirer.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2">
                <Label className="text-xs">Nom</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="edit-threat-name-input" />
              </div>
              <div>
                <Label className="text-xs">Catégorie</Label>
                <Select value={form.category} onValueChange={(x) => setForm({ ...form, category: x })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="edit-threat-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{THREAT_CATEGORY_KEYS.map((k) => <SelectItem key={k} value={k}>{THREAT_CATEGORIES[k]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Gravité</Label>
                <Select value={form.severity} onValueChange={(x) => setForm({ ...form, severity: x })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="edit-threat-severity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SEVERITY_KEYS.map((k) => <SelectItem key={k} value={k}>{SEVERITY[k].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Statut</Label>
                <Select value={form.status} onValueChange={(x) => setForm({ ...form, status: x })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="edit-threat-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{THREAT_STATUS_KEYS.map((k) => <SelectItem key={k} value={k}>{THREAT_STATUS[k].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Type d'acteur (optionnel)</Label>
                <Select value={form.default_actor_type} onValueChange={(x) => setForm({ ...form, default_actor_type: x })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="edit-threat-actor-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">Non renseigné</SelectItem>
                    {ACTOR_TYPE_KEYS.map((k) => <SelectItem key={k} value={k}>{ACTOR_TYPES[k]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" rows={2} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setEditOpen(false)}>Annuler</Button>
            <Button className="rounded-sm" onClick={() => save.mutate()} disabled={!form?.name || save.isPending} data-testid="save-threat-edit-button">
              {save.isPending ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link vulnerability dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="link-vuln-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Associer une vulnérabilité</DialogTitle>
            <DialogDescription>THREAT —CAN_EXPLOIT→ VULNERABILITY. La confiance est saisie par l'analyste.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Vulnérabilité</Label>
              <Select value={linkForm.vulnerability_id} onValueChange={(v) => setLinkForm({ ...linkForm, vulnerability_id: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="link-vuln-select"><SelectValue placeholder="Choisir une vulnérabilité…" /></SelectTrigger>
                <SelectContent>
                  {linkable.map((v) => <SelectItem key={v.id} value={v.id}>{v.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Confiance</Label>
              <Select value={linkForm.confidence} onValueChange={(v) => setLinkForm({ ...linkForm, confidence: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="link-confidence-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LINK_CONFIDENCE_KEYS.map((k) => <SelectItem key={k} value={k}>{LINK_CONFIDENCE[k]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Note (optionnelle)</Label>
              <Textarea value={linkForm.note} onChange={(e) => setLinkForm({ ...linkForm, note: e.target.value })} className="mt-1 rounded-sm" rows={2} data-testid="link-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setLinkOpen(false)}>Annuler</Button>
            <Button className="rounded-sm gap-2" onClick={() => link.mutate()} disabled={!linkForm.vulnerability_id || link.isPending} data-testid="confirm-link-vuln-button">
              <Link2 className="h-4 w-4" /> Associer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link technique dialog */}
      <Dialog open={techOpen} onOpenChange={setTechOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="link-technique-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Associer une technique ATT&CK</DialogTitle>
            <DialogDescription>THREAT —USES→ TECHNIQUE. Référentiel MITRE officiel (lecture seule).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Technique</Label>
              <Select value={techRef} onValueChange={setTechRef}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="link-technique-select"><SelectValue placeholder="Choisir une technique…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {linkableTech.map((x) => <SelectItem key={x.stix_id} value={x.stix_id}>{x.external_id} — {x.name} ({tMatrix(x.matrix)})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setTechOpen(false)}>Annuler</Button>
            <Button className="rounded-sm gap-2" onClick={() => linkTech.mutate()} disabled={!techRef || linkTech.isPending} data-testid="confirm-link-technique-button">
              <Link2 className="h-4 w-4" /> Associer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mt-6"><ScenariosPanel endpoint={`/threats/${id}/scenarios`} queryKey={["threat-scenarios", id]} /></div>
    </AppShell>
  );
}
